import { Component, effect, ChangeDetectionStrategy } from '@angular/core';
import { AttributeComponent } from '@xanadu/core';
import { Register } from '@xanadu/core';
import { Folder, ImportData, Cast, PublishData, AspectCheck, Preview } from '@xanadu/model';
import { Observable, of } from 'rxjs';

@Component({
    selector: 'app-folder-panel',
    templateUrl: './folder-panel.component.html',
    styleUrls: ['./folder-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Register('folder-panel')
export class FolderPanelComponent extends AttributeComponent<Folder> {
    tabFolder = { caption: 'Folder', hidden: false };
    tabAttribute = { caption: 'Attribute', active: false, hidden: false };
    tabPreview = { caption: 'Preview', hidden: false };
    tabPublish = { caption: 'Publish', hidden: false };
    tabImport = { caption: 'Import', hidden: false };

    metaAspect: any = { id: 'aspects', name: 'Aspects', info: 'Folder aspects' };
    metaAttribute: any = { id: 'attributes', name: 'Attributes', info: 'Folder attributes' };
    metaGraphql: any = { id: 'graphqlType', name: 'GraphQL Type', info: 'GraphQL type name, starts with a letter, can contain letters, numbers and underscores only.', pattern: '^[A-Za-z][a-zA-Z0-9_]*$', updateOn: 'blur' }; //id can NOT be 'key', it is a preserved word

    enableEndpoint: boolean = false;
    enableCast: boolean = false;
    enableImport: boolean = false;

    private response: any
    private cast?: Cast;
    private importData: any;
    private validated?: string;
    private aspectCheck?: AspectCheck;

    constructor(
    ) {
        super();

        effect(() => {
            const endpoint = this.treeSignal.importEndpoint();

            this.enableEndpoint = !!endpoint;
        });

        effect(() => {
            const importResponse = this.treeSignal.importResponse();
            this.response = importResponse;
            this.enableCast = !!this.cast && !!importResponse;
        });

        effect(() => {
            const importCast = this.treeSignal.importCast();
            this.cast = importCast;
            this.enableCast = !!importCast && !!this.response;
        });


        effect(() => {
            const importResult = this.treeSignal.importResult();
            this.importData = importResult;
            this.enableImport = !!importResult;
        });
    }

    onRequest() {
        this.treeSignal.importRequest.set({});
    }

    onConvert() {
        this.treeSignal.importConvert.set({});
    }

    onImport() {
        if (this.importData) {
            if (this.importData instanceof Array) {
                this.treeSignal.importMany.set(this.importData);
            } else {
                this.treeSignal.import.set(this.importData);
            }
        }
    }

    onPermission(): void {
        const folder = this.node();
        if (folder) {
            this.doPermission(Folder, folder);
        }
    }

    onCascade(): void {
        const folder = this.node();
        if (folder) {
            this.doCascade(Folder);
        }
    }

    onAdmission(): void {
        const folder = this.node();
        if (folder) {
            this.doAdmission(Folder, folder);
        }
    }

    validate: (x: any) => Observable<boolean> = (value) => {
        const folder = this.node();

        if (this.validated === value) {
            return of(true);
        }
        this.validated = value;
        return folder?._id ? this.dataService.checkGraphQL(folder._id, value, folder.siteId) : of(false);
    }

    protected override setup(folder: Folder): void {
        super.setup(folder);

        if (folder) {
            this.aspectCheck = new AspectCheck(folder);
        }

        this.tabs = [this.tabFolder];
        this.tabSelected = this.tabFolder;

        if (folder?.prototypeId) {
            this.tabs?.push(this.tabAttribute);
            this.tabs?.push(this.tabPreview);
            this.tabs?.push(this.tabPublish);
            this.tabs?.push(this.tabImport);
        }

        if (this.permissible) {
            this.tabs?.push(this.tabSecurity);
            this.tabs?.push(this.tabAccess);
        }

        const model = this.model();

        model.custome._import = model.custome._import || {};

        this.enableCast = !!this.cast && !!this.response;
        this.enableImport = !!this.importData;
    }

    protected override load(folder: Folder, form: any): string | undefined {
        super.load(folder, form);

        const { _import, publish, aspects, attributes, graphqlType, preview, ...rest } = form;

        if (folder?.prototypeId) {
            this.aspectCheck?.check(aspects, attributes);
            folder.aspects = aspects;
            folder.attributes = attributes;
            folder.publish = new PublishData(publish);
            folder.graphqlType = graphqlType;
            folder.import = new ImportData(_import);
            folder.preview = new Preview(preview);
        }

        return;
    }
}
